import { useState, useContext, useMemo } from "react";
import * as XLSX from "xlsx";
import uuid from "react-uuid";
import { db, timestamp } from "../../firebase/config.js";
import { client } from "../../hooks/Client";
import useSettings from "../../hooks/Settings";
import Location from "../../hooks/Location";
import ScrollToTop from "../../hooks/ScrollToTop";
import { useHistory } from "react-router-dom";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { useFirestoreID, useFirestore } from "../../firebase/useFirestore";
import notDeleted from "../../helpers/notDeleted";
import PageHeader from "../../components/layout/PageHeader.jsx";
import PageNotice from "../../components/layout/PageNotice";
import EffectDatabase from "../../components/meetstandaard/EffectDatabase";
import ImportMappingTable from "../../components/measurementPlan/import/ImportMappingTable.jsx";
import ImportPersonaTable from "../../components/measurementPlan/import/ImportPersonaTable.jsx";
import parseDatasetWorkbook from "../../helpers/import/parseDatasetWorkbook";
import matchColumnsToMeetstandaard, { flattenMeetstandaardCatalog } from "../../helpers/import/matchColumnsToMeetstandaard";
import findPersonaByEmail from "../../helpers/import/findPersonaByEmail";
import buildImportPlan from "../../helpers/import/buildImportPlan";
import executeImportPlan from "../../helpers/import/executeImportPlan";
import parseImportTimestamp from "../../helpers/import/parseImportTimestamp";
import { clearHierarchyDataCache } from "../../hooks/useHierarchyData";

/**
 * Deccos Pro page: import a dataset (.xlsx) into a single meetmoment. Parses the two-row
 * header, auto-links columns to the meetstandaard, lets the user review/edit the links,
 * and on save materialises the questionnaire structure + writes the responses.
 */
const ImportMeasureMoment = () => {
    // Context
    const [, setSaved] = useContext(SavedIcon);

    // Hooks
    ScrollToTop();
    const history = useHistory();
    const deccosPro = useSettings().deccosPro;
    const path = Location();
    const researchId = path[3];
    const momentId = path[4];
    const database = EffectDatabase();
    const flatEffects = useMemo(() => flattenMeetstandaardCatalog(database), [database]);
    const researches = useFirestoreID("Research", researchId || "123");
    const research = researches && researches[0];
    const personas = useFirestore("Personas");
    const personaData = useFirestore("PersonaData");

    // State
    const [parsed, setParsed] = useState(/** @type {object|null} */ (null));
    const [mappings, setMappings] = useState([]);
    const [fileName, setFileName] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    // emailKey → chosen persona id or 'new'. Empty until the user overrides a match.
    const [personaChoices, setPersonaChoices] = useState(/** @type {Object<string,string>} */ ({}));

    // Unique respondents in the file, each with the persona it matches by email (or null).
    // Orphaned PersonaData (whose persona was deleted) is ignored, so the UI shows the same
    // match the import will actually make.
    const respondents = useMemo(() => {
        if (!parsed) return [];
        const livePersonaIds = new Set((Array.isArray(personas) ? personas : []).map((p) => p.ID));
        const matchablePersonaData = (Array.isArray(personaData) ? personaData : []).filter((pd) =>
            livePersonaIds.has(pd.PersonaID)
        );
        const pid = parsed.metaCols.personaId;
        /** @type {Map<string, string>} emailKey → first-seen email spelling */
        const seen = new Map();
        parsed.dataRows.forEach((r) => {
            const email = String(r[pid] ?? "").trim();
            if (!email) return;
            const key = email.toLowerCase();
            if (!seen.has(key)) seen.set(key, email);
        });
        return [...seen.entries()].map(([emailKey, email]) => ({
            emailKey,
            email,
            matchedPersonaId: findPersonaByEmail(matchablePersonaData, email),
        }));
    }, [parsed, personaData, personas]);

    if (!deccosPro) {
        return (
            <div className="main">
                <div className="main-container">
                    <PageHeader heading={"Dataset importeren"} description={"Onderzoeksdata importeren."} />
                    <PageNotice text={"Deze functie is onderdeel van Deccos Pro."} />
                </div>
            </div>
        );
    }

    // Read + parse + auto-match the uploaded workbook.
    const handleFile = (e) => {
        setError("");
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (!database || database.length === 0) {
            setError("De meetstandaard is nog niet geladen. Wacht even en probeer opnieuw.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            try {
                const wb = XLSX.read(new Uint8Array(reader.result), { type: "array", cellDates: true });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true });
                const result = parseDatasetWorkbook(rows);
                const suggestions = matchColumnsToMeetstandaard(result.effectBands, database);
                setParsed(result);
                setMappings(suggestions);
                setPersonaChoices({}); // start each file with the auto-matched persona choices
                setFileName(file.name);
            } catch (err) {
                setParsed(null);
                setMappings([]);
                setError(err.message || "Kon het bestand niet verwerken.");
            }
        };
        reader.onerror = () => setError("Kon het bestand niet lezen.");
        reader.readAsArrayBuffer(file);
    };

    const handleMappingChange = (colIndex, effect, question) => {
        setMappings((prev) => prev.map((m) => (m.colIndex === colIndex ? { ...m, effect, question } : m)));
    };

    const matchedCount = mappings.filter((m) => m.effect && m.question).length;
    const respondentCount = respondents.length;

    const handleSave = async () => {
        setError("");
        setSaving(true);
        try {
            if (!research) throw new Error("Onderzoek niet gevonden.");
            if (!parsed) throw new Error("Upload eerst een bestand.");

            // The file's OrganisatieID is ignored — all writes use the URL's client.

            // Pre-flight: every timestamp must parse (analytics filters on date).
            const tsCol = parsed.metaCols.timestamp;
            if (tsCol !== undefined) {
                const badIdx = parsed.dataRows.findIndex((r) => {
                    const v = r[tsCol];
                    return v !== "" && v !== null && v !== undefined && !parseImportTimestamp(v);
                });
                if (badIdx >= 0) {
                    throw new Error(`Rij ${badIdx + 3} heeft een ongeldige datum in de Timestamp-kolom.`);
                }
            }

            if (matchedCount === 0) {
                throw new Error("Geen enkele kolom is gekoppeld aan de meetstandaard.");
            }

            // Fetch existing tenant docs so structure + personas are reused, not duplicated.
            // Personas are fetched too so orphaned PersonaData (left behind by a deleted
            // persona) doesn't shadow the creation of a fresh persona on re-import.
            const [effSnap, fldSnap, rowSnap, colSnap, pdSnap, persSnap] = await Promise.all([
                db.collection("OutputEffects").where("CompagnyID", "==", client).get(),
                db.collection("QuestionnaireFields").where("CompagnyID", "==", client).get(),
                db.collection("MatrixQuestionRows").where("CompagnyID", "==", client).get(),
                db.collection("MatrixQuestionColumns").where("CompagnyID", "==", client).get(),
                db.collection("PersonaData").where("CompagnyID", "==", client).get(),
                db.collection("Personas").where("CompagnyID", "==", client).get(),
            ]);
            const existing = {
                effects: effSnap.docs.map((d) => d.data()).filter(notDeleted),
                fields: fldSnap.docs.map((d) => d.data()).filter(notDeleted),
                // Rijen en kolommen worden zacht verwijderd; een import mag
                // geen antwoorden aan een verwijderde rij hangen.
                // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
                rows: rowSnap.docs.map((d) => d.data()).filter(notDeleted),
                columns: colSnap.docs.map((d) => d.data()).filter(notDeleted),
                personaData: pdSnap.docs.map((d) => d.data()),
                personas: persSnap.docs.map((d) => d.data()),
            };

            const plan = buildImportPlan({
                parsed,
                columnMappings: mappings,
                research,
                momentId,
                client,
                existing,
                newId: uuid,
                // Only the user's explicit picks override the email match; untouched rows
                // fall back to buildImportPlan's freshly-fetched email matching.
                personaOverrides: personaChoices,
            });

            await executeImportPlan(plan, { db, timestamp, researchDocId: research.docid });

            // De dashboards halen responses via fetchHierarchyData met een
            // sessie-cache — zonder invalidatie blijven ze stale tot een harde
            // browser-refresh (inbox #83).
            clearHierarchyDataCache();

            setSaved("flex");
            history.push(`/${client}/researchresults/${researchId}`);
        } catch (err) {
            setError(err.message || "Importeren mislukt.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={"Dataset importeren"}
                    description={
                        "Upload een dataset (.xlsx) voor dit meetmoment. De kolommen worden automatisch " +
                        "gekoppeld aan de meetstandaard. Controleer de koppelingen en sla op."
                    }
                />

                <div className="table-container dashboard-container import-upload-container">
                    <h3>Bestand</h3>
                    <input type="file" accept=".xlsx" onChange={handleFile} />
                    {fileName && <p className="import-file-name">{fileName}</p>}
                </div>

                {error && (
                    <div className="table-container dashboard-container">
                        <p className="import-error">{error}</p>
                    </div>
                )}

                {parsed && (
                    <div className="table-container dashboard-container">
                        <h3>Controleren en importeren</h3>
                        <p className="import-summary">
                            {matchedCount} van {mappings.length} kolommen gekoppeld · {respondentCount} respondenten
                        </p>

                        <h4 className="import-section-title">Koppelingen controleren</h4>
                        <ImportMappingTable mappings={mappings} flatEffects={flatEffects} onChange={handleMappingChange} />

                        {respondents.length > 0 && (
                            <>
                                <h4 className="import-section-title">Deelnemers koppelen</h4>
                                <p className="import-section-description">
                                    Elke respondent wordt op e-mailadres aan een bestaande deelnemer gekoppeld.
                                    Controleer de koppeling of kies een andere deelnemer.
                                </p>
                                <ImportPersonaTable
                                    respondents={respondents}
                                    personas={Array.isArray(personas) ? personas : []}
                                    choices={personaChoices}
                                    onChange={(emailKey, value) =>
                                        setPersonaChoices((prev) => ({ ...prev, [emailKey]: value }))
                                    }
                                />
                            </>
                        )}

                        <button className="import-save-button" onClick={handleSave} disabled={saving || matchedCount === 0}>
                            {saving ? "Bezig met importeren…" : "Importeren"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportMeasureMoment;
